// Panorix logomark + wordmark — uses official light/dark SVG assets in /logos/.
import { useTheme } from '@/contexts/ThemeContext';

const DARK_SRC  = '/logos/panorix-dark.svg';
const LIGHT_SRC = '/logos/panorix-light.svg';

// Native SVG aspect ratio: 1300 × 280
const RATIO = 1300 / 280;

function resolveSrc(forceTheme, isDark) {
  if (forceTheme === 'dark')  return DARK_SRC;
  if (forceTheme === 'light') return LIGHT_SRC;
  return isDark ? DARK_SRC : LIGHT_SRC;
}

/** Full logo (logomark + wordmark + tagline). Theme-aware.
 *  Pass forceTheme="dark" on dark-only surfaces (e.g. the always-dark landing page). */
export function PanorixLogo({ width = 240, height, showWordmark = true, className = '', forceTheme }) {
  const { isDark } = useTheme();
  const src = resolveSrc(forceTheme, isDark);
  // Logomark-only: clip a square crop of the leftmost portion.
  if (!showWordmark) {
    const size = width;
    const scaledFullWidth = Math.round(size * RATIO);
    return (
      <div className={className} style={{ width: size, height: size, overflow: 'hidden', display: 'inline-block' }}>
        <img src={src} alt="Panorix"
          width={scaledFullWidth} height={size}
          style={{ width: scaledFullWidth, height: size, display: 'block' }} />
      </div>
    );
  }
  // Wordmark: always preserve native aspect ratio.
  const computedHeight = Math.round(width / RATIO);
  return (
    <img src={src} alt="Panorix — AI-Powered Infrastructure Control Tower"
      width={width} height={computedHeight} className={className}
      style={{ width, height: computedHeight, display: 'block', maxWidth: '100%' }} />
  );
}

/** Stacked variant for sidebars. */
export function PanorixLogoStacked({ logomarkSize = 180, forceTheme }) {
  const { isDark } = useTheme();
  const src = resolveSrc(forceTheme, isDark);
  const h = Math.round(logomarkSize / RATIO);
  return (
    <div className="flex items-center justify-center px-2" style={{ minHeight: 56 }}>
      <img src={src} alt="Panorix" width={logomarkSize} height={h} style={{ width: logomarkSize, height: h, display: 'block' }} />
    </div>
  );
}

/** Compact header variant. */
export function PanorixLogoCompact({ size = 140, forceTheme }) {
  const { isDark } = useTheme();
  const src = resolveSrc(forceTheme, isDark);
  const h = Math.round(size / RATIO);
  return (
    <div className="flex items-center gap-2.5">
      <img src={src} alt="Panorix" width={size} height={h} style={{ width: size, height: h, display: 'block' }} />
    </div>
  );
}
